<template>
  <Container
    id="heading"
  >
    <HeroCard
      :heading="t('jc.view.creator.hero.title')"
      :paragraph="t('jc.view.creator.hero.text')"
      :image="{
        src: 'images/view/creator/hero_banner.png',
        alt: t('jc.view.creator.hero.banner.alt'),
      }"
      :float="{
        src: 'images/view/creator/hero_float.png',
        alt: t('jc.view.creator.hero.float.alt'),
      }"
      :label="t('jc.view.creator.hero.button.label')"
      :icon="'pen-field'"
      :to="config.public.creatorApplyUrl"
    />
  </Container>

  <Container
    id="features"
  >
    <FeatureTree
      :title="t('jc.view.creator.benefits.title')"
      :items="featureItems"
      alternating
    />
  </Container>

  <Container
    id="requirements-streamer"
  >
    <ContentCard
      :heading="t('jc.view.creator.requirements.streamer.title')"
      :paragraph="t('jc.view.creator.requirements.streamer.text')"
    >
      <IconList
        :items="streamerRequirements"
      />
    </ContentCard>
  </Container>

  <Container
    id="requirements-youtuber"
  >
    <ContentCard
      :heading="t('jc.view.creator.requirements.youtuber.title')"
      :paragraph="t('jc.view.creator.requirements.youtuber.text')"
    >
      <IconList
        :items="youtuberRequirements"
      />
    </ContentCard>
  </Container>

  <Container
    id="more-information"
  >
    <div
      class="flex flex-col gap-8"
    >
      <ContentCard
        :heading="t('jc.view.gameDeveloper.moreInformation.title')"
        :paragraph="t('jc.view.gameDeveloper.moreInformation.text')"
      />
      <div
        class="grid grid-cols-1 mobile:gird-cols-2 laptop:grid-cols-3 desktop:grid-cols-4 gap-x-4 gap-y-16 justify-center"
      >
        <template
          v-for="(card, index) in informationCards"
          :key="index"
        >
          <ShowcaseCard
            v-bind="card"
          />
        </template>
      </div>
    </div>
  </Container>

  <Container
    id="call-to-action"
  >
    <ImageCard
      :heading="t('jc.view.creator.callToAction.title')"
      :paragraph="t('jc.view.creator.callToAction.text')"
      :image="{
        src: 'images/view/creator/call_to_action_background.png',
        alt: t('jc.view.creator.callToAction.background.alt'),
      }"
    >
      <NuxtLink
        :target="'_blank'"
        :to="config.public.creatorApplyUrl"
        :title="t('jc.component.participateCallCard.link.title')"
        class="bg-primary hover:bg-primary-highlighted active:bg-primary-selected text-white
          text-center rounded py-2 px-4 mt-10 mx-auto font-semibold"
      >
        <font-awesome-icon
          icon="pen-field"
          class="mr-2"
        />
        <span>{{ t('jc.view.creator.callToAction.button.label') }}</span>
      </NuxtLink>
    </ImageCard>
  </Container>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const { t } = useI18n()
const localePath = useLocalePath()
const config = useRuntimeConfig()

definePageMeta({
  nameKey: 'creator',
  head: {
    imageUrl: '/images/view/creator/meta_preview.png',
  },
})

// items in 'features' (feature tree) section
const featureItems = [
  {
    icon: 'rabbit-fast',
    content: {
      title: t('jc.view.creator.benefits.content.title'),
      text: t('jc.view.creator.benefits.content.text'),
      image: {
        src: 'images/view/creator/benefit/content.png',
        alt: t('jc.view.creator.benefits.content.image.alt'),
      },
    },
  },
  {
    icon: 'comments-question-check',
    content: {
      title: t('jc.view.creator.benefits.support.title'),
      text: t('jc.view.creator.benefits.support.text'),
      image: {
        src: 'images/view/creator/benefit/support.png',
        alt: t('jc.view.creator.benefits.support.image.alt'),
      },
    },
  },
  {
    icon: 'gamepad-modern',
    content: {
      title: t('jc.view.creator.benefits.gameplay.title'),
      text: t('jc.view.creator.benefits.gameplay.text'),
      image: {
        src: 'images/view/creator/benefit/gameplay.png',
        alt: t('jc.view.creator.benefits.gameplay.image.alt'),
      },
    },
  },
  {
    icon: 'comment-plus',
    content: {
      title: t('jc.view.creator.benefits.suggestion.title'),
      text: t('jc.view.creator.benefits.suggestion.text'),
      image: {
        src: 'images/view/creator/benefit/suggestion.png',
        alt: t('jc.view.creator.benefits.suggestion.image.alt'),
      },
    },
  },
  {
    icon: 'chart-line-up',
    content: {
      title: t('jc.view.creator.benefits.advertisement.title'),
      text: t('jc.view.creator.benefits.advertisement.text'),
      image: {
        src: 'images/view/creator/benefit/advertisement.png',
        alt: t('jc.view.creator.benefits.advertisement.image.alt'),
      },
    },
  },
  {
    icon: 'sparkles',
    content: {
      title: t('jc.view.creator.benefits.rewards.title'),
      text: t('jc.view.creator.benefits.rewards.text'),
      image: {
        src: 'images/view/creator/benefit/rewards.png',
        alt: t('jc.view.creator.benefits.rewards.image.alt'),
      },
    },
  },
  {
    icon: 'coins',
    content: {
      title: t('jc.view.creator.benefits.shop.title'),
      text: t('jc.view.creator.benefits.shop.text'),
      image: {
        src: 'images/view/creator/benefit/shop.png',
        alt: t('jc.view.creator.benefits.shop.image.alt'),
      },
    },
  },
  {
    icon: 'calendar-star',
    content: {
      title: t('jc.view.creator.benefits.events.title'),
      text: t('jc.view.creator.benefits.events.text'),
      image: {
        src: 'images/view/creator/benefit/events.png',
        alt: t('jc.view.creator.benefits.events.image.alt'),
      },
    },
  },
  {
    icon: 'camera-movie',
    content: {
      title: t('jc.view.creator.benefits.optimization.title'),
      text: t('jc.view.creator.benefits.optimization.text'),
      image: {
        src: 'images/view/creator/benefit/optimization.png',
        alt: t('jc.view.creator.benefits.optimization.image.alt'),
      },
    },
  },
]

// items in 'requirements-streamer' (icon list) section
const streamerRequirements = [
  {
    icon: 'eye',
    text: t('jc.view.creator.requirements.streamer.viewer.text'),
  },
  {
    icon: 'users',
    text: t('jc.view.creator.requirements.streamer.follower.text'),
  },
  {
    icon: 'photo-film',
    text: t('jc.view.creator.requirements.streamer.content.text'),
  },
  {
    icon: 'battery',
    text: t('jc.view.creator.requirements.streamer.interest.text'),
  },
]

// items in 'requirements-youtuber' (icon list) section
const youtuberRequirements = [
  {
    icon: 'eye',
    text: t('jc.view.creator.requirements.youtuber.views.text'),
  },
  {
    icon: 'users',
    text: t('jc.view.creator.requirements.youtuber.subscribers.text'),
  },
  {
    icon: 'photo-film',
    text: t('jc.view.creator.requirements.youtuber.content.text'),
  },
  {
    icon: 'battery',
    text: t('jc.view.creator.requirements.youtuber.interest.text'),
  },
]

// items in 'more-information' (showcase cards) section
const informationCards = [
  {
    icon: 'gamepad-modern',
    text: t('jc.view.creator.moreInformation.card.game.text'),
    label: t('jc.view.creator.moreInformation.card.game.button.label'),
    to: localePath({ name: 'game' }),
  },
  {
    icon: 'users',
    text: t('jc.view.creator.moreInformation.card.team.text'),
    label: t('jc.view.creator.moreInformation.card.team.button.label'),
    to: localePath({ name: 'team' }),
  },
  {
    icon: 'newspaper',
    text: t('jc.view.creator.moreInformation.card.weekly_review.text'),
    label: t('jc.view.creator.moreInformation.card.weekly_review.button.label'),
    to: localePath({ name: 'news' }),
  },
  {
    icon: { prefix: 'fab', iconName: 'discord' },
    text: t('jc.view.creator.moreInformation.card.discord.text'),
    label: t('jc.view.creator.moreInformation.card.discord.button.label'),
    to: config.public.discordUrl,
  },
]
</script>
